/**
 * Currency formatting utilities for crypto trading platform.
 *
 * Different crypto assets trade at different price levels and require different
 * precision levels for meaningful display of price movements.
 */

/**
 * Configuration for decimal precision per product.
 * This maps product IDs to their required decimal places for dollar amounts.
 */
const PRODUCT_DECIMAL_CONFIG: Record<string, number> = {
  // 4 decimals for lower-priced altcoins
  'ADA-USDC': 4,
  'SUI-USDC': 4,
  'XRP-USDC': 4,

  // 3 decimals for mid-priced assets
  'SOL-USDC': 3,
  'AVAX-USDC': 3,
  'LINK-USDC': 3,

  // 2 decimals for higher-priced assets (default)
  'BTC-USDC': 2,
  'ETH-USDC': 2,
};

/**
 * Default decimal places for products not explicitly configured.
 */
const DEFAULT_DECIMALS = 2;

/**
 * Formats a dollar amount according to the product's precision requirements.
 *
 * @param value - The dollar amount to format
 * @param productId - The product ID (e.g., 'ADA-USDC', 'BTC-USDC')
 * @returns Formatted string with appropriate decimal places and thousand separators
 *
 * @example
 * formatDollarAmount(1.2345, 'ADA-USDC') // "1.2345" (4 decimals)
 * formatDollarAmount(15.678, 'LINK-USDC') // "15.678" (3 decimals)
 * formatDollarAmount(42000.12, 'BTC-USDC') // "42,000.12" (2 decimals with comma)
 */
export function formatDollarAmount(value: number, productId: string): string {
  const decimals = PRODUCT_DECIMAL_CONFIG[productId] ?? DEFAULT_DECIMALS;
  return value.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/**
 * Formats a dollar amount with currency symbol according to product precision.
 *
 * @param value - The dollar amount to format
 * @param productId - The product ID (e.g., 'ADA-USDC', 'BTC-USDC')
 * @returns Formatted string with $ prefix, thousand separators, and appropriate decimal places
 *
 * @example
 * formatDollar(1.2345, 'ADA-USDC') // "$1.2345"
 * formatDollar(15.678, 'LINK-USDC') // "$15.678"
 * formatDollar(42000.12, 'BTC-USDC') // "$42,000.12"
 * formatDollar(1938948.00, 'BTC-USDC') // "$1,938,948.00"
 */
export function formatDollar(value: number, productId: string): string {
  return `$${formatDollarAmount(value, productId)}`;
}

/**
 * Gets the configured decimal places for a product.
 * Useful for calculations or validation.
 *
 * @param productId - The product ID
 * @returns Number of decimal places configured for this product
 */
export function getDecimalPlaces(productId: string): number {
  return PRODUCT_DECIMAL_CONFIG[productId] ?? DEFAULT_DECIMALS;
}
